import {history, historyKeymap} from '@codemirror/commands'
import {EditorView, keymap, placeholder, type ViewUpdate} from '@codemirror/view'
import {readOnly} from '@github-ui/code-mirror/extensions/readonly'
import {spacingControls} from '@github-ui/code-mirror/extensions/spacing-controls'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useTrackingRef} from '@github-ui/use-tracking-ref'
import type {FileType} from '@github-ui/use-unified-file-select'
import {clsx} from 'clsx'
import {forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react'

import {caretFlashExtension} from './codemirror/extensions/caret-flash'
import {dropPositionExtension} from './codemirror/extensions/drop-position'
import {markdownContinuationExtension} from './codemirror/extensions/markdown-continuation'
import {markdownKeymap} from './codemirror/extensions/markdown-keymap'
import {isModPressedField, modPressedExtension} from './codemirror/extensions/mod-pressed'
import {pasteLinksExtension} from './codemirror/extensions/paste-links'
import {
  getCurrentSelectionContext,
  initialSelectionContext,
  selectionContextChanged,
  selectionContextExtension,
} from './codemirror/extensions/selection-context'
import {singleLineEditorDefaultKeymap, singleLineEditorExtension} from './codemirror/extensions/single-line-editor'
import {styleContentsExtension} from './codemirror/extensions/style-contents'
import {uploadPlaceholderExtension} from './codemirror/extensions/upload-placeholder'
import {visitLinkExtension} from './codemirror/extensions/visit-link'
import {useCompartment} from './codemirror/hooks/use-compartment'
import {SelectionContextProvider} from './context/SelectionContext'
import {ErrorFallback} from './ErrorFallback'
import {type FileUploadResult, useFileHandling} from './file-handler/useFileHandler'
import styles from './HybridMarkdownEditor.module.css'
import {Header} from './toolbar/Header'
import {Toolbar} from './toolbar/Toolbar'
import type {MarkdownViewMode} from './toolbar/ViewSwitch'

export interface HybridMarkdownEditorProps {
  'aria-describedby'?: string
  disabled?: boolean
  onChange?: (value: string) => void
  onChangeViewMode?: (newViewMode: MarkdownViewMode) => void
  placeholder?: string
  onUploadFile?: (file: File, onProgress?: (progressBytes: number) => void) => Promise<FileUploadResult>
  acceptedFileTypes?: FileType[]
  onFilesRejected?: (files: File[], rejectedTypes: string[]) => void
  value: string
}

export interface HybridMarkdownEditorRef {
  view: EditorView | null
  focus: () => void
  reset: () => void
  scrollIntoView: (opts?: ScrollIntoViewOptions) => void
  moveCursorToEnd: () => void
}

const DEFAULT_ACCEPTED_FILE_TYPES: FileType[] = ['image/*', 'video/*']

const InnerHybridMarkdownEditor = forwardRef<HybridMarkdownEditorRef, HybridMarkdownEditorProps>(
  function InnerHybridMarkdownEditor(
    {
      value,
      onChange,
      disabled,
      onChangeViewMode,
      placeholder: placeholderText,
      'aria-describedby': ariaDescribedBy,
      acceptedFileTypes = DEFAULT_ACCEPTED_FILE_TYPES,
      onUploadFile,
      onFilesRejected,
    },
    ref,
  ) {
    const [selectionContext, setSelectionContext] = useState(initialSelectionContext)

    /** Stable tracking reference to `onChange` so we don't have to update editor extensions when the prop changes. */
    const onChangeRef = useTrackingRef(onChange)
    const onUpdate = useCallback(
      (update: ViewUpdate) => {
        if (update.docChanged) onChangeRef.current?.(update.state.doc.toString())

        // Check for selection context changes
        if (update.transactions.some(tr => selectionContextChanged(tr))) {
          const newContext = getCurrentSelectionContext(update.state)
          setSelectionContext(newContext)
        }
      },
      [onChangeRef],
    )

    const [view, setView] = useState<EditorView | null>(null)

    // Each extension that depends on prop values must be contained in a compartment to avoid reinitializing the editor
    // when the props change. Compartments are constant references so safe to use as dependencies.
    const placeholderCompartment = useCompartment(
      view,
      useMemo(() => placeholder(placeholderText ?? ''), [placeholderText]),
    )

    const readonlyCompartment = useCompartment(
      view,
      useMemo(() => (disabled ? readOnly() : []), [disabled]),
    )

    const a11yAttributesCompartment = useCompartment(
      view,
      useMemo(
        () =>
          EditorView.contentAttributes.of({
            'aria-label': 'Markdown content',
            ...(ariaDescribedBy ? {'aria-describedby': ariaDescribedBy} : {}),
          }),
        [ariaDescribedBy],
      ),
    )

    const extensions = useMemo(
      () => [
        singleLineEditorExtension,
        styleContentsExtension,
        uploadPlaceholderExtension,
        dropPositionExtension,
        caretFlashExtension,
        selectionContextExtension,
        pasteLinksExtension,
        markdownContinuationExtension,
        visitLinkExtension(),
        history(),
        // eslint-disable-next-line react-hooks/refs
        EditorView.updateListener.of(onUpdate),
        a11yAttributesCompartment,
        modPressedExtension,
        EditorView.contentAttributes.from(isModPressedField, isModPressed => ({
          // Note: Compartments appear to get applied after first render, which causes a flash of unstyled content when
          // `class` is applied inside a compartment. When we add support for a `className` prop, we'll want to keep
          // this line _in addition_ to the new compartment.
          class: clsx(styles.content, 'markdown-body', isModPressed && styles.modPressed),
        })),
        // Configure as a prose editor rather than a code editor by enabling spellcheck/autocorrect
        EditorView.contentAttributes.of({
          spellcheck: 'true',
          autocorrect: 'on',
          autocapitalize: 'sentences',
          writingsuggestions: 'true',
        }),
        spacingControls({
          indentUnit: 2,
          indentWithTabs: false,
          lineWrapping: true,
        }),
        placeholderCompartment,
        readonlyCompartment,
        keymap.of(markdownKeymap),
        keymap.of(singleLineEditorDefaultKeymap),
        keymap.of(historyKeymap),
      ],
      // IMPORTANT: These dependencies should not change during the lifecycle of the component
      [onUpdate, placeholderCompartment, readonlyCompartment, a11yAttributesCompartment],
    )

    const containerRef = useRef<HTMLDivElement>(null)

    const fileHandler = useFileHandling({
      value,
      view,
      disabled,
      onUploadFile,
      acceptedFileTypes,
      onFilesRejected,
    })

    // Wrap drop target props to fix the dragleave issue when dragging over child elements
    const wrappedDropTargetProps = useMemo(() => {
      const dropProps = fileHandler?.dropTargetProps
      if (!dropProps) return undefined

      return {
        ...dropProps,
        onDragLeave: (event: React.DragEvent<HTMLDivElement>) => {
          // Only call the original handler if the drag is truly leaving the container
          // Check if the relatedTarget (where the drag is moving to) is outside the container
          if (!(event.currentTarget as Node).contains(event.relatedTarget as Node)) {
            dropProps.onDragLeave?.(event as unknown as React.DragEvent<HTMLDivElement>)
          }
        },
        onDragOver: dropProps.onDragOver,
        onDragEnter: dropProps.onDragEnter,
        onDrop: dropProps.onDrop,
      }
    }, [fileHandler?.dropTargetProps])

    const initialValueRef = useRef(value)
    useEffect(
      function initializeEditor() {
        const editorView = new EditorView({
          parent: containerRef.current!,
          doc: initialValueRef.current,
          extensions,
        })

        setView(editorView)

        return () => {
          editorView.destroy()
          setView(null)
        }
      },
      // IMPORTANT: These dependencies should not change during the lifecycle of the component
      [extensions],
    )

    useEffect(function syncValue() {
      if (view?.state.doc.toString() !== value && onChange) {
        view?.update([view.state.update({changes: {from: 0, to: view.state.doc.length, insert: value}})])
        // Note no dependencies: this runs on every render intentionally. If we had [view, value] as deps, the effect
        // would not run if the user makes a change and then the parent re-renders with the same value (discarding the change).
      }
    })

    useImperativeHandle(
      ref,
      () => ({
        view,
        focus: () => view?.focus(),
        reset: () => view?.dispatch({changes: {from: 0, to: view.state.doc.length, insert: ''}}),
        scrollIntoView: opts => view?.dom?.scrollIntoView(opts),
        moveCursorToEnd: () => view?.dispatch({selection: {anchor: view.state.doc.length}}),
      }),
      [view],
    )

    return (
      <>
        <Header onChangeViewMode={onChangeViewMode}>
          <SelectionContextProvider value={selectionContext}>
            <Toolbar
              view={view}
              acceptedFileTypes={acceptedFileTypes}
              handleFileUpload={fileHandler?.handleFileUpload}
            />
          </SelectionContextProvider>
        </Header>

        <div
          ref={containerRef}
          className={fileHandler?.isDraggedOver ? styles.isDraggedOver : undefined}
          {...(wrappedDropTargetProps ?? {})}
          {...(fileHandler?.pasteTargetProps ?? {})}
        />
      </>
    )
  },
)

export const HybridMarkdownEditor = forwardRef<HybridMarkdownEditorRef, HybridMarkdownEditorProps>(
  // IMPORTANT: Keep this component as minimal as possible, with no logic that might possibly throw an error, since any
  // errors thrown in this component wouldn't be caught by the error boundary
  function HybridMarkdownEditor({onChangeViewMode, ...restProps}, ref) {
    return (
      <div className={styles.container}>
        <ErrorBoundary fallback={<ErrorFallback onChangeViewMode={onChangeViewMode} />}>
          <InnerHybridMarkdownEditor ref={ref} onChangeViewMode={onChangeViewMode} {...restProps} />
        </ErrorBoundary>
      </div>
    )
  },
)

try{ InnerHybridMarkdownEditor.displayName ||= 'InnerHybridMarkdownEditor' } catch {}
try{ InnerHybridMarkdownEditor.displayName ||= 'InnerHybridMarkdownEditor' } catch {}
try{ HybridMarkdownEditor.displayName ||= 'HybridMarkdownEditor' } catch {}
try{ HybridMarkdownEditor.displayName ||= 'HybridMarkdownEditor' } catch {}